import { useState } from "react";
import { jobsApi, ApiError } from "@/api";
import type { Drawing } from "@/types";
import { Button, TextField } from "@/components/ui";
import { drawingCode } from "@/lib/jobs";
import { FormDrawer } from "./FormDrawer";

/** Edit a single drawing's title (Addendum 2 §4.4) — opened by clicking a
 *  drawing card. Type is intentionally NOT editable here. Writes via
 *  update_drawing only when the title changed. */
export function EditDrawingDrawer({
  drawing,
  jobNumber,
  setNumber,
  onClose,
  onSaved,
}: {
  drawing: Drawing;
  jobNumber: number;
  setNumber: number;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [title, setTitle] = useState(drawing.title);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | undefined>();

  async function submit() {
    if (saving) return;
    if (title.trim() === drawing.title) {
      onClose();
      return;
    }
    setSaving(true);
    setError(undefined);
    try {
      await jobsApi.updateDrawing(drawing.id, { title: title.trim() });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save the drawing.");
      setSaving(false);
    }
  }

  return (
    <FormDrawer
      title={`Edit ${drawingCode(jobNumber, setNumber, drawing.drawing_number)}`}
      busy={saving}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={submit} loading={saving}>Save</Button>
        </>
      }
    >
      {error ? <p className="leads__error" role="alert" onClick={() => setError(undefined)}>{error}</p> : null}

      <section className="job-drawer__section">
        <TextField
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Drawing title"
        />
      </section>
    </FormDrawer>
  );
}
